import React, { useCallback, useState, useEffect, useRef } from 'react'
import {
  Keyboard,
  NativeSyntheticEvent,
  Platform,
  TextInputContentSizeChangeEventData,
} from 'react-native'
import { Input } from 'react-native-elements'
import { SvgProps } from 'react-native-svg'
import Icon from 'react-native-vector-icons/MaterialIcons'

import I18n from 'APP/I18n'
import { useFilePicker } from 'APP/Hooks/FilePicker'
import { ChatPost, ChatSelectedFile, useChatContext } from 'APP/Lib/Context'
import InputAddress from 'APP/Components/Chat/InputAddress'

import { css, DefaultTheme, useTheme } from 'styled-components/native'
import { Images } from 'APP/Themes'
import styled from 'styled-components/native'
import { isMobile, isWeb } from 'APP/Helpers/checkPlatform'
import InputFile from 'APP/Components/Chat/InputFile'
import Config from 'APP/Config'
import Animated, {
  useAnimatedKeyboard,
  useAnimatedStyle,
  interpolate,
} from 'react-native-reanimated'

const Container = styled(Animated.View)`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: 8px;
  background-color: transparent;
  max-width: 800px;
  width: 100%;
`

const RightIconButton = styled.TouchableOpacity`
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  /* wrapper is 48x48, icon is 20x20, 48-20/2 = 14. Needs an extra 2 pixels to be right-aligned with the chat elements */
  margin-right: 2px;
`

const getMaxInputHeight = (theme: DefaultTheme) => {
  const maxInputHeight = 5 * theme.fonts.lineHeight.large
  const mobileSpacing = isMobile() ? theme.metrics.baseMargin * 2 : 0

  return maxInputHeight + mobileSpacing
}

interface StyledInputProps {
  $inputHeight: number | undefined
  $isCurrentlyDisabled: boolean
}

const StyledInput = styled(Input).attrs(({ theme }: { theme: DefaultTheme }) => {
  const minHeight = theme.fonts.lineHeight.large + theme.metrics.baseMargin * 2

  return {
    containerStyle: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    inputContainerStyle: {
      borderBottomWidth: 0,
      backgroundColor: 'transparent',
      maxHeight: getMaxInputHeight(theme),
    },
    rightIconContainerStyle: {
      paddingLeft: 0,
      paddingRight: 0,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
      height: minHeight,
      alignSelf: 'flex-end',
    },
  }
})<StyledInputProps>`
  color: ${({ $isCurrentlyDisabled, theme }) =>
    $isCurrentlyDisabled ? theme.colors.disabled : theme.colors.textSecondary};
  background-color: ${({ $isCurrentlyDisabled, theme }) =>
    $isCurrentlyDisabled ? theme.colors.contrastElementBg : theme.colors.elementsBg};
  border-radius: 25px;
  border-width: 0px;
  /* Keep iOS bottom spacing outside UITextView's scrollable content. */
  border-bottom-width: ${({ theme }) => (Platform.OS === 'ios' ? theme.metrics.baseMargin : 0)}px;
  border-bottom-color: ${({ $isCurrentlyDisabled, theme }) =>
    $isCurrentlyDisabled ? theme.colors.contrastElementBg : theme.colors.elementsBg};
  padding-left: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-right: ${({ theme }) => theme.metrics.baseMargin}px;
  min-height: ${({ theme }) => theme.fonts.lineHeight.large + theme.metrics.baseMargin * 2}px;
  max-height: ${({ theme }) => getMaxInputHeight(theme)}px;
  font-family: ${({ theme }) => theme.fonts.fontTypes.base};
  font-size: ${({ theme }) => theme.fonts.fontSize.large}px;
  line-height: ${({ theme }) => theme.fonts.lineHeight.large}px;
  ${({ $inputHeight }) =>
    $inputHeight !== undefined &&
    css`
      height: ${$inputHeight}px;
    `}
`

interface InputBarProps {
  scrollToBottom?: () => void
}

interface FilePickerOptions {
  submitCallback: (fileData: ChatSelectedFile) => void
  showFiles: boolean
  fileInputRef: React.RefObject<HTMLInputElement | null>
  resize: boolean
}

interface FilePickerResult {
  openFilePicker: () => void
  onFileChangeSubmit: (event: React.ChangeEvent<HTMLInputElement>) => void
  resetFile?: (value: null) => void
}

interface SendMessageIconProps extends SvgProps {
  accentFillLine: string
}

// TODO: {Typescript} remove this local type adapter once FilePicker.js and
// FilePicker.web.js are migrated and expose a shared typed contract.
const typedUseFilePicker = useFilePicker as unknown as (
  options: FilePickerOptions
) => FilePickerResult
const SendMessageIcon = Images.sendMessage as React.FC<SendMessageIconProps>

const InputBar: React.FC<InputBarProps> = ({ scrollToBottom }) => {
  const {
    sendMessage,
    onFilePicked,
    isConnected,
    isInactiveAndEpisodeInactive,
    hasEligibleBrands,
    lastPost,
    selectedAddress,
    selectedFile,
    setSelectedFile,
    typingEvent,
    channelId,
  } = useChatContext()
  const theme = useTheme()
  const keyboard = useAnimatedKeyboard()

  const animatedContainerStyle = useAnimatedStyle(() => {
    const paddingBottom = interpolate(keyboard.height.value, [0, 300], [theme.insets.bottom, 10])
    return {
      paddingBottom,
    }
  })

  const [forceEnabled, setForceEnabled] = useState<boolean>(false)
  const [inputTextHeight, setInputTextHeight] = useState<number>(0)
  const [message, setMessage] = useState<string>()
  const hasMessage = (message?.length ?? 0) > 0
  const rightIcon = hasMessage ? 'send' : 'attach-file'

  const inputFile = useRef<HTMLInputElement>(null)

  const isTextOrFileQuestion = ['text.v1', 'prompt_text', 'prompt_file'].includes(
    lastPost?.props?.type ?? ''
  )
  const isAnsweringQuestion = Boolean(
    lastPost?.props?.utterance?.includes('utter_ask_') && !isTextOrFileQuestion
  )

  const isSystemUserTyping =
    typingEvent?.broadcast?.channel_id === channelId &&
    typingEvent?.data?.user_id === Config.MATTERMOST_SYSTEM_USER

  // A soft disable can be overridden by clicking on the input
  const isSoftDisabled = isAnsweringQuestion
  const isHardDisabled =
    !isConnected || isInactiveAndEpisodeInactive || isAnsweringQuestion || isSystemUserTyping
  const isCurrentlyDisabled = (isSoftDisabled || isHardDisabled) && !forceEnabled

  const onSelectFile = (fileData: ChatSelectedFile) => {
    setSelectedFile!(fileData)
  }

  const { openFilePicker, onFileChangeSubmit, resetFile } = typedUseFilePicker({
    submitCallback: onSelectFile,
    showFiles: true,
    fileInputRef: inputFile,
    resize: true,
  })

  // Scrolls to bottom of chat screen when the keyboard is visible
  // "keyboardDidShow" event works on both Android and iOS, but not sure why `keyboardWillShow` doesn't on Android...
  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => {
      scrollToBottom?.()
    })

    return () => {
      keyboardDidShowListener.remove()
    }
  }, [scrollToBottom])

  const prevLastPost = useRef<ChatPost>(undefined)
  useEffect(() => {
    const receivedNewPost = prevLastPost?.current && lastPost?.id !== prevLastPost.current?.id
    if (receivedNewPost) {
      setForceEnabled(false)
    }
    prevLastPost.current = lastPost
  }, [lastPost])

  const onChange = useCallback((txt: string) => setMessage(txt), [setMessage])

  const onFocus = () => {
    if (isSoftDisabled) {
      setForceEnabled(true)
    }
  }

  const handleSubmit = () => {
    if (!isConnected || isInactiveAndEpisodeInactive || isSystemUserTyping) return
    if (hasMessage) {
      sendMessage!({ message })
      setMessage('')
      setInputTextHeight(30)
    }
  }

  const handleClickIcon = () => {
    if (hasMessage) {
      handleSubmit()
    } else {
      openFilePicker()
    }
  }

  const renderRightIcon = () => {
    if (isHardDisabled) return null
    return (
      <RightIconButton
        disabled={isCurrentlyDisabled}
        onPress={handleClickIcon}
        testID={hasMessage ? 'send-message-right-icon' : 'attach-file-right-icon'}
        activeOpacity={0.5}
        accessibilityLabel={
          hasMessage
            ? I18n.t('AccessibilityIcons.chat.send')
            : I18n.t('AccessibilityIcons.chat.attachFile')
        }
      >
        {hasMessage ? (
          <SendMessageIcon
            accentFillLine={
              isCurrentlyDisabled ? theme.colors.buttonSecondaryDisabled : theme.colors.accent
            }
            width={theme.metrics.icon.small}
            height={theme.metrics.icon.small}
          />
        ) : (
          <Icon
            name={rightIcon}
            size={theme.metrics.icon.small}
            color={isCurrentlyDisabled ? theme.colors.buttonSecondaryDisabled : theme.colors.accent}
          />
        )}
      </RightIconButton>
    )
  }

  const getPlaceholderText = () => {
    if (isSystemUserTyping) {
      return I18n.t('ChatScreen.input.placeholder.waitForTypingToComplete')
    }
    if (!isConnected) {
      return I18n.t('Navigation.offlineWarning')
    }
    if (isInactiveAndEpisodeInactive) {
      return hasEligibleBrands
        ? I18n.t('Common.inactiveCoverage.multiBrand')
        : I18n.t('Common.inactiveCoverage.singleBrand')
    }
    if (isCurrentlyDisabled) {
      return I18n.t('ChatScreen.input.placeholder.tap')
    }
    return I18n.t('ChatScreen.input.placeholder.send')
  }

  const onContentSizeChange = (e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
    setInputTextHeight(e?.nativeEvent?.contentSize?.height)
    scrollToBottom?.()
  }

  const onInputFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFileChangeSubmit(e)
  }

  const cleanUpFile = () => {
    setSelectedFile!(null)
    if (resetFile) resetFile(null)
  }

  const onSubmitFile = () => {
    onFilePicked!(selectedFile!)
    setSelectedFile!(null)
  }

  let inputHeight: number | undefined
  switch (Platform.OS) {
    case 'ios':
      inputHeight = undefined
      break
    case 'android': {
      const padding = theme.metrics.baseMargin * 2
      const lineHeight = theme.fonts.lineHeight.large
      inputHeight = Math.round((inputTextHeight - padding) / lineHeight) * lineHeight + padding
      break
    }
    case 'web':
    default:
      inputHeight = inputTextHeight
      break
  }

  const renderInput = () => {
    if (selectedFile) {
      return <InputFile fileData={selectedFile} onCancel={cleanUpFile} onSubmit={onSubmitFile} />
    }
    if (selectedAddress) {
      return <InputAddress />
    }
    return (
      <StyledInput
        $inputHeight={inputHeight}
        $isCurrentlyDisabled={isCurrentlyDisabled}
        enterKeyHint="next"
        value={message}
        placeholder={getPlaceholderText()}
        onFocus={onFocus}
        onChangeText={onChange}
        selectionColor={theme.colors.text}
        placeholderTextColor={theme.colors.disabled}
        multiline
        blurOnSubmit={isWeb()}
        onContentSizeChange={onContentSizeChange}
        rightIcon={renderRightIcon() ?? undefined}
        disabled={isHardDisabled}
        testID="INPUT_BAR"
        renderErrorMessage={false}
        onSubmitEditing={handleSubmit}
        autoCorrect={false}
      />
    )
  }

  return (
    <Container style={animatedContainerStyle}>
      {isWeb() && (
        <input
          key={selectedFile?.fileName || 'file-input'} // Forces re-render when file changes
          type="file"
          accept="image/*,.pdf"
          ref={inputFile}
          hidden
          onChange={onInputFileChange}
        />
      )}
      {renderInput()}
    </Container>
  )
}

export default InputBar
